import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginAsUser } from "../services/authService";
import { registerFCMToken } from "../services/notificationService";
import { APIClient, initializeApiClient } from "../utils/api/apiClient";
import { IMPERSONATION_KEYS } from "../utils/Common/impersonation";

type AppRole = "admin" | "gc" | "sub" | "lender" | "owner" | "inspector";

const ROLE_REDIRECT: Record<AppRole, string> = {
  admin: "/admin",
  gc: "/gc-dashboard",
  sub: "/lien-waiver",
  lender: "/bank-dashboard",
  owner: "/owner-dashboard",
  inspector: "/project-inspection",
};

interface ApiError {
  message?: string;
}

interface LoginAsTokens {
  accessToken: string;
  refreshToken: string;
}

interface LoginAsUser {
  roles?: string[];
  [key: string]: unknown;
}

interface LoginAsResponseData {
  data: {
    user?: LoginAsUser;
    tokens?: LoginAsTokens;
  };
}

const useLoginAs = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const loginAs = async (userId: string, preferredRole?: string): Promise<void> => {
    setLoading(true);
    setError("");

    const { data, error: apiError } = (await loginAsUser(userId)) as {
      data: LoginAsResponseData | null;
      error: ApiError | null;
    };

    if (apiError || !data?.data?.tokens || !data?.data?.user) {
      setError(apiError?.message || "Failed to switch user.");
      setLoading(false);
      return;
    }

    const { user, tokens } = data.data;
    // Multi-role users: sign in as the role chosen in the Switch Role list.
    const roles = user.roles ?? [];
    const role = (
      preferredRole && roles.includes(preferredRole) ? preferredRole : (roles[0] ?? "")
    ) as AppRole;

    // Stash the real admin session so "Back to Admin" can restore it —
    // only on the first switch, so a nested switch can't overwrite it.
    if (localStorage.getItem(IMPERSONATION_KEYS.FLAG) !== "true") {
      const adminToken = localStorage.getItem("token");
      const adminRole = localStorage.getItem("role");
      if (adminToken && adminRole) {
        localStorage.setItem(IMPERSONATION_KEYS.ADMIN_TOKEN, adminToken);
        localStorage.setItem(
          IMPERSONATION_KEYS.ADMIN_REFRESH_TOKEN,
          localStorage.getItem("refreshToken") ?? "",
        );
        localStorage.setItem(IMPERSONATION_KEYS.ADMIN_ROLE, adminRole);
        localStorage.setItem(
          IMPERSONATION_KEYS.ADMIN_USER,
          localStorage.getItem("user") ?? "",
        );
        localStorage.setItem(IMPERSONATION_KEYS.FLAG, "true");
      }
    }

    localStorage.setItem("token", tokens.accessToken);
    localStorage.setItem("refreshToken", tokens.refreshToken ?? "");
    localStorage.setItem("role", role);
    localStorage.setItem("user", JSON.stringify({ ...user, role }));
    localStorage.setItem("authChanged", Date.now().toString());

    APIClient.setApiToken(tokens.accessToken);
    APIClient.setRole(role);
    initializeApiClient(role);

    // Re-associate this device's push token with the impersonated user —
    // the FCM token itself doesn't change, only who it belongs to.
    const fcmToken = localStorage.getItem("fcm_token");
    if (fcmToken) {
      registerFCMToken(fcmToken, true).catch(() => {});
    }

    window.dispatchEvent(new CustomEvent("auth-ready"));

    setLoading(false);
    navigate(ROLE_REDIRECT[role] || "/", { replace: true });
  };

  return { loginAs, loading, error };
};

export default useLoginAs;
